import moment from 'moment-jalaali';

// Iran has used a fixed UTC+03:30 offset since 2023. Scheduling is always in Tehran.
export const TEHRAN_OFFSET_MINUTES = 210;
export const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
export const JALALI_WEEKDAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
export const MIN_JALALI_YEAR = 1300;
export const MAX_JALALI_YEAR = 1600;

export function tehranNow(now = new Date()): moment.Moment {
  return moment.utc(now).utcOffset(TEHRAN_OFFSET_MINUTES).locale('en').seconds(0).milliseconds(0);
}

export function tehranFromIso(value: string): moment.Moment | null {
  if (!value || !/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return null;
  // moment-jalaali's format parser accepts format strings, not Moment's ISO_8601 sentinel.
  const parsed = moment.utc(value, ['YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DDTHH:mm:ss.SSZ', 'YYYY-MM-DDTHH:mm:ss.SZ', 'YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DDTHH:mmZ'], true).utcOffset(TEHRAN_OFFSET_MINUTES).locale('en');
  return parsed.isValid() ? parsed : null;
}

export function jalaliDateKey(value: moment.Moment): string {
  return value.clone().locale('en').format('jYYYY/jMM/jDD');
}

export function jalaliDateTimeToIso(year: number, month: number, day: number, time: string): string | null {
  if (!Number.isInteger(year) || year < MIN_JALALI_YEAR || year > MAX_JALALI_YEAR ||
      !Number.isInteger(month) || month < 0 || month > 11 || !Number.isInteger(day) ||
      day < 1 || day > moment.jDaysInMonth(year, month) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) return null;
  const parts = `${year}/${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')} ${time}`;
  // Parse wall-clock fields in UTC first, then attach Tehran's offset without moving them.
  // Parsing through the device timezone would move the chosen date for users abroad.
  const parsed = moment.utc(parts, 'jYYYY/jMM/jDD HH:mm', true).locale('en').utcOffset(TEHRAN_OFFSET_MINUTES, true);
  return parsed.isValid() ? parsed.toISOString() : null;
}

export function calendarMonth(year: number, month: number): moment.Moment {
  const iso = jalaliDateTimeToIso(year, month, 1, '00:00');
  if (!iso) throw new Error('Invalid Jalali month');
  return tehranFromIso(iso)!;
}

export function calendarDays(view: moment.Moment): moment.Moment[] {
  const first = view.clone().jDate(1).startOf('day');
  // Moment's locale week can start on Sunday; this calendar always starts on Saturday.
  const leadingDays = (first.day() + 1) % 7;
  const start = first.subtract(leadingDays, 'days');
  return Array.from({ length: 42 }, (_, index) => start.clone().add(index, 'days'));
}

export function addTehranDays(value: string, days: number, now = new Date()): string | null {
  const base = value ? tehranFromIso(value) : tehranNow(now);
  return base && Number.isInteger(days) ? base.clone().add(days, 'days').toISOString() : null;
}

export function jalaliDateTimeLabel(value: string): string {
  const date = tehranFromIso(value);
  if (!date) return '';
  const digits = (text: string) => text.replace(/\d/g, digit => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);
  return `${digits(date.format('jYYYY/jMM/jDD'))}، ساعت ${digits(date.format('HH:mm'))}`;
}
